import { Icon } from 'components/Icon';
import { classNames, React, useEffect, useRef, useState } from 'libs/react';
import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import './AutocompleteMultiSelect.scss';
/**
 * @component AutocompleteMultiSelect
 * @since 1.0.0
 * @hidden
 */
export function AutocompleteMultiSelect(props) {
    const { name, values, options, required, disabled, placeholder } = Object.assign({ placeholder: '', values: [] }, props);
    const [searchText, setSearchText] = useState('');
    const [open, setOpen] = useState(false);
    const [overlayLayout, setOverlayLayout] = useState(null);
    const containerRef = useRef(null);
    const inputRef = useRef(null);
    const overlayContainerRef = useRef(null);
    /**
     * @function onBlur
     * @since 1.0.0
     * @hidden
     */
    function onBlur() {
        if (props.onBlur) {
            props.onBlur();
        }
    }
    /**
     * @function onFocus
     * @since 1.0.0
     * @hidden
     */
    function onFocus() {
        if (props.onFocus) {
            props.onFocus();
        }
    }
    /**
     * @function onChange
     * @since 1.0.0
     * @hidden
     */
    function toggleOption(value) {
        let newValues = [];
        if (values.find((val) => val === value)) {
            newValues = values.filter((val) => val !== value);
        }
        else {
            newValues = [...values, value];
        }
        if (props.onChange) {
            props.onChange(newValues);
        }
    }
    /**
     * @function handleCloseOverlay
     * @since 1.0.0
     * @hidden
     */
    const handleCloseOverlay = () => {
        setOpen(false);
        onBlur();
        setSearchText('');
    };
    /**
     * @function handleInputFocus
     * @since 1.0.0
     * @hidden
     */
    function handleInputFocus() {
        onFocus();
        setOpen(true);
    }
    const optionsFormatted = useMemo(() => options
        .map((option) => {
        var _a;
        const checked = (_a = values.find((val) => val === option.value)) !== null && _a !== void 0 ? _a : false;
        return Object.assign(Object.assign({}, option), { checked: Boolean(checked) });
    })
        .filter((option) => option.label.toLowerCase().includes(searchText.toLowerCase())), [values, options, searchText]);
    // Shown in the field while it is closed, and as the placeholder while
    // searching. Taken from every option, not only the filtered ones.
    const selectedLabel = useMemo(() => options
        .filter((option) => values.includes(option.value))
        .map((option) => option.label)
        .join(', '), [values, options]);
    useEffect(() => {
        if (!open) {
            setOverlayLayout(null);
            return;
        }
        function updateOverlayLayout() {
            if (!containerRef.current) {
                return;
            }
            const rect = containerRef.current.getBoundingClientRect();
            const padding = 16;
            const spaceBelow = window.innerHeight - rect.bottom - padding;
            const maxHeight = Math.min(300, Math.max(spaceBelow, 120));
            setOverlayLayout({
                top: rect.bottom,
                left: rect.left,
                width: rect.width,
                maxHeight
            });
        }
        updateOverlayLayout();
        window.addEventListener('resize', updateOverlayLayout);
        window.addEventListener('scroll', updateOverlayLayout, true);
        return () => {
            window.removeEventListener('resize', updateOverlayLayout);
            window.removeEventListener('scroll', updateOverlayLayout, true);
        };
    }, [open]);
    useEffect(() => {
        if (!open) {
            return;
        }
        function handleMouseDown(event) {
            var _a, _b;
            const target = event.target;
            if (((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(target)) ||
                ((_b = overlayContainerRef.current) === null || _b === void 0 ? void 0 : _b.contains(target))) {
                return;
            }
            handleCloseOverlay();
        }
        document.addEventListener('mousedown', handleMouseDown);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
        };
    }, [open]);
    const overlay = open && overlayLayout ? (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "autocomplete-multi-select-overlay-container", "data-closeable-ignore": true, style: {
                top: overlayLayout.top,
                left: overlayLayout.left,
                width: overlayLayout.width
            } },
            React.createElement("div", { className: "autocomplete-multi-select-overlay", ref: overlayContainerRef, style: { maxHeight: overlayLayout.maxHeight } }, optionsFormatted.map((option, index) => (React.createElement("div", { key: `option-${index}`, className: "autocomplete-multi-select-checkbox-container", onClick: () => toggleOption(option.value) },
                option.checked ? (React.createElement(Icon, { value: "fas square-check", className: "autocomplete-multi-select-checkbox-icon" })) : (React.createElement(Icon, { value: "square", className: "autocomplete-multi-select-uncheck-icon" })),
                React.createElement("span", null, option.label)))))))) : null;
    return (React.createElement("div", { className: classNames('autocomplete-multi-select-container', {
            'autocomplete-multi-select-container--open': open
        }), ref: containerRef },
        React.createElement("div", { className: "autocomplete-multi-select-input-container" },
            React.createElement("input", { type: "text", name: name, value: open ? searchText : selectedLabel, onFocus: handleInputFocus, onChange: (e) => {
                    setSearchText(e.target.value);
                    if (!open) {
                        setOpen(true);
                    }
                }, ref: inputRef, placeholder: (open && selectedLabel) || placeholder, required: required, disabled: disabled })),
        overlay && createPortal(overlay, document.body)));
}
